<template>
  <div>
    <BaseFormSelect
      id="webhook-select"
      v-model="selectedWebhookId"
      :horizontal="true"
      :small="true"
      :disabled="saving || loading"
      internal-platform-input
      name="Conversion Webhook"
      label="Conversion Webhook"
      :options="webhookOptions"
      width="9"
      has-tooltip
    />

    <BaseButtonLoader
      variant="primary"
      size="sm"
      horizontal
      :disabled="!hasChanges"
      :loading="saving"
      @click="saveWebhookSelection"
    >
      Update
    </BaseButtonLoader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import BaseButtonLoader from '@/components/BaseButtonLoader.vue'
import BaseFormSelect from '@/components/form/BaseFormSelect.vue'

export default {
  name: 'CpaWebhookSelector',

  components: {
    BaseButtonLoader,
    BaseFormSelect
  },

  props: {
    accountId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      selectedWebhookId: null,
      saving: false,
      loading: false
    }
  },

  computed: {
    ...mapGetters('sellerWebhooks', [
      'hasWebhookConfigured',
      'availableWebhooks',
      'sellerWebhook'
    ]),

    webhookOptions() {
      const options = this.availableWebhooks.map(webhook => ({
        value: webhook.id,
        text: webhook.name || webhook.endPointUrl || webhook.id
      }))

      const placeholderText = this.availableWebhooks.length === 0
        ? 'No webhooks available'
        : 'Select a webhook'

      options.unshift({
        value: null,
        text: placeholderText,
        disabled: true
      })

      return options
    },

    hasChanges() {
      return this.selectedWebhookId && this.selectedWebhookId !== this.sellerWebhook?.ringbaWebhookId
    }
  },

  async created() {
    await this.loadWebhookData()
  },

  methods: {
    ...mapActions('sellerWebhooks', [
      'fetchRingbaWebhooks',
      'selectWebhook',
      'fetchSellerWebhook'
    ]),

    async loadWebhookData() {
      this.loading = true
      try {
        // Fetch seller's saved webhook and available Ringba webhooks in parallel
        await Promise.all([
          this.fetchSellerWebhook({ accountId: this.accountId }),
          this.fetchRingbaWebhooks({ accountId: this.accountId })
        ])

        // Pre-select current webhook if exists
        if (this.sellerWebhook?.ringbaWebhookId) {
          this.selectedWebhookId = this.sellerWebhook.ringbaWebhookId
        }
      } catch (error) {
        showErrorMessage(error.response?.data?.title || error.message || 'Failed to load webhook data')
      } finally {
        this.loading = false
      }
    },

    async saveWebhookSelection() {
      if (!this.selectedWebhookId) return

      try {
        this.saving = true
        await this.selectWebhook({
          accountId: this.accountId,
          ringbaWebhookId: this.selectedWebhookId
        })
        showSuccessMessage('Webhook saved successfully')
        this.$emit('webhook-saved')
      } catch (error) {
        showErrorMessage(error.response?.data?.title || error.message || 'Failed to save webhook')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
